/**
 * Product catalog helpers (T-365): the form fields of a record from its schema, the draft the
 * inputs hold as text, the PATCH body, and the server's field errors. The server validates; this
 * only types what the person typed so a price is sent as a number.
 */

import { callableErrorCode } from "./invite";

export interface SchemaProp {
  type?: string | string[];
  enum?: unknown[];
}

export interface RecordSchema {
  type?: string;
  required?: string[];
  properties?: Record<string, SchemaProp>;
}

export type FieldKind = "text" | "number" | "boolean" | "select" | "readonly";

export interface FormField {
  key: string;
  kind: FieldKind;
  required: boolean;
  /** The enum values; a draft holds their text. */
  options?: unknown[];
}

type Data = Record<string, unknown>;

/** Every data key once, in the order first seen: the columns of the catalog table. */
export const columnKeys = (items: { data: Data }[]): string[] => [...new Set(items.flatMap((item) => Object.keys(item.data)))];

/** A cell's text; objects and arrays show as JSON. */
export function cellText(value: unknown, labels: { yes: string; no: string }): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? labels.yes : labels.no;
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function kindOf(prop: SchemaProp | undefined, value: unknown): FieldKind {
  if (prop?.enum) return "select";
  const type = [prop?.type ?? typeof value].flat()[0];
  if (type === "string") return "text";
  if (type === "number" || type === "integer") return "number";
  if (type === "boolean") return "boolean";
  return "readonly";
}

/** One field per schema property, then any data key the schema does not name (read-only). */
export function formFields(schema: RecordSchema | undefined, data: Data): FormField[] {
  const properties = schema?.properties ?? {};
  const required = new Set(schema?.required);
  const keys = [...new Set([...Object.keys(properties), ...Object.keys(data)])];
  return keys.map((key) => {
    const prop = properties[key];
    const kind = prop || !schema ? kindOf(prop, data[key]) : "readonly";
    return { key, kind, required: required.has(key), ...(prop?.enum && { options: prop.enum }) };
  });
}

const isEditable = (field: FormField) => field.kind !== "readonly";

/** The text each editable input starts with. */
export function draftOf(data: Data, fields: FormField[]): Record<string, string> {
  return Object.fromEntries(fields.filter(isEditable).map(({ key }) => [key, data[key] === undefined || data[key] === null ? "" : String(data[key])]));
}

function typed(field: FormField, text: string): unknown {
  if (field.kind === "boolean") return text === "true";
  if (field.kind === "select") return field.options?.find((option) => String(option) === text) ?? text;
  if (field.kind === "number") return Number.isFinite(Number(text)) ? Number(text) : text;
  return text;
}

/** The record data the draft means: untouched values and read-only fields stay; an emptied field is dropped. */
export function dataFromDraft(fields: FormField[], draft: Record<string, string>, original: Data): Data {
  const data = { ...original };
  for (const field of fields.filter(isEditable)) {
    const text = draft[field.key] ?? "";
    if (text === "") delete data[field.key];
    else data[field.key] = typed(field, text);
  }
  return data;
}

const sameData = (a: Data, b: Data) => JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort());

/** Save sends the data; Approve sends the status, with the data when the person edited it (one request). */
export function buildPatch(action: "save" | "approve", fields: FormField[], draft: Record<string, string>, original: Data) {
  const data = dataFromDraft(fields, draft, original);
  if (action === "save") return { data };
  return sameData(data, original) ? { status: "approved" as const } : { data, status: "approved" as const };
}

/** The rule the server named per field, from a rejected save (422 schema_invalid). */
export function fieldErrorsOf(error: unknown): Record<string, string> {
  const errors = (error as { details?: { errors?: unknown } } | null)?.details?.errors;
  if (!Array.isArray(errors)) return {};
  return Object.fromEntries(errors.map((e: { field: string; error: string }) => [e.field, e.error]));
}

const ERROR_KEYS: Record<string, string> = {
  "permission-denied": "productCatalog.errors.forbidden",
  "not-found": "productCatalog.errors.notFound",
  "already-exists": "productCatalog.errors.outdated",
  "invalid-argument": "productCatalog.errors.invalid",
};

/** The message key for a failed catalog call; `fallback` is the sentence for any other failure. */
export const productErrorKey = (error: unknown, fallback: string): string => ERROR_KEYS[callableErrorCode(error)] ?? fallback;
